import { ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { combineLatest } from 'rxjs';

import {
  ActivitiesDataScope,
  DefaultActivitiesAndNotificationsFilters,
  getStoreScope,
  TrackingActivitiesActions,
} from '@core/activities-and-notifications-data';
import { ActivitiesListComponent } from '@core/activities-and-notifications-ui';
import type { TrackingActivity } from '@core/activities-types';
import { filterBoolean } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import {
  EmptyStateComponent,
  ScrollableDirective,
  SpinnerComponent,
} from '@core/user-interface-legacy';

import { ActivityPageDataService } from '../../../data/single-requisition/activity-page-data.service';
import { ActivityFacade } from '../../../facades/single-requisition/activity.facade';

@Component({
  selector: 'rt-requisitions-activities-container',
  templateUrl: './requisitions-activities-container.component.html',
  styleUrls: ['./requisitions-activities-container.component.less'],
  imports: [
    EmptyStateComponent,
    InfiniteScrollModule,
    SpinnerComponent,
    ActivitiesListComponent,
    TranslatePipe,
  ],
})
export class RequisitionsActivitiesContainerComponent {
  private readonly destroyRef = inject(DestroyRef);
  public isLoaded: boolean;
  public activities: TrackingActivity[] = [];
  public canManageCandidate: boolean;

  private requisitionId: number;

  private filters = { ...DefaultActivitiesAndNotificationsFilters };

  constructor(
    public scrollable: ScrollableDirective,
    private data: ActivityPageDataService,
    private store: Store,
    private cdRef: ChangeDetectorRef,
    private activityFacade: ActivityFacade,
  ) {}

  public ngOnInit() {
    combineLatest([
      this.data.activities$,
      this.data.manageCandidateAbility$,
      this.data.requisition$,
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([activities, canManageCandidate, requisition]) => {
        this.activities = activities;
        this.canManageCandidate = canManageCandidate;
        this.requisitionId = requisition.id;
        this.cdRef.detectChanges();
      });

    this.data.isLoaded$.pipe(filterBoolean, takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.isLoaded = true;
      this.cdRef.detectChanges();
    });
  }

  public openActivity(activity: TrackingActivity): void {
    this.activityFacade.openActivity(activity);
  }

  public loadMore(): void {
    this.filters = {
      ...this.filters,
      page: this.filters.page! + 1,
    };
    this.loadActivities();
  }

  private loadActivities(): void {
    const requisitionActivitiesScope = getStoreScope(
      ActivitiesDataScope.Requisition,
      this.requisitionId,
    );
    this.store.dispatch(
      TrackingActivitiesActions.fetchRequisitionTrackingActivities({
        payload: {
          params: {
            requisitionId: this.requisitionId,
            filters: this.filters,
          },
          scope: requisitionActivitiesScope,
          replace: false,
        },
      }),
    );
  }
}
